import { createRoute, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router'
import type { AnyRoute } from '@tanstack/react-router'
import { i18n, registerCommonI18n } from './shared/i18n/i18n'
import sv from './features/reports/locales/sv.json'
import en from './features/reports/locales/en.json'
import tableSv from './features/table/locales/sv.json'
import tableEn from './features/table/locales/en.json'
import observationReportsSv from './features/observation-reports/locales/sv.json'
import observationReportsEn from './features/observation-reports/locales/en.json'
import otherReportsSv from './features/other-reports/locales/sv.json'
import otherReportsEn from './features/other-reports/locales/en.json'
import advancedSv from './features/advanced/locales/sv.json'
import advancedEn from './features/advanced/locales/en.json'
import statisticsTabsSv from './features/statistics-tabs/locales/sv.json'
import statisticsTabsEn from './features/statistics-tabs/locales/en.json'
import animalsSv from './shared/animals/locales/sv.json'
import animalsEn from './shared/animals/locales/en.json'
import { ANIMALS_NAMESPACE } from './shared/animals/animalVocabulary'
import { parseSpeciesSlug } from './features/observation-reports/species-tabs'
import { ActiveDistrictProvider } from './shared/api/activeDistrict'

let i18nRegistered = false
function registerStatisticsI18n() {
  if (i18nRegistered) return
  i18nRegistered = true
  registerCommonI18n()
  i18n.addResourceBundle('sv', 'statistics', sv, true, true)
  i18n.addResourceBundle('en', 'statistics', en, true, true)
  i18n.addResourceBundle('sv', 'table', tableSv, true, true)
  i18n.addResourceBundle('en', 'table', tableEn, true, true)
  i18n.addResourceBundle('sv', 'observationReports', observationReportsSv, true, true)
  i18n.addResourceBundle('en', 'observationReports', observationReportsEn, true, true)
  i18n.addResourceBundle('sv', 'otherReports', otherReportsSv, true, true)
  i18n.addResourceBundle('en', 'otherReports', otherReportsEn, true, true)
  i18n.addResourceBundle('sv', 'advanced', advancedSv, true, true)
  i18n.addResourceBundle('en', 'advanced', advancedEn, true, true)
  i18n.addResourceBundle('sv', 'statisticsTabs', statisticsTabsSv, true, true)
  i18n.addResourceBundle('en', 'statisticsTabs', statisticsTabsEn, true, true)
  i18n.addResourceBundle('sv', ANIMALS_NAMESPACE, animalsSv, true, true)
  i18n.addResourceBundle('en', ANIMALS_NAMESPACE, animalsEn, true, true)
}

export interface StatisticsRoutesOptions {
  /**
   * A hook returning the area the user has chosen in the app's area switcher. The
   * shell supplies it because the switcher belongs to another domain package; every
   * statistics request is scoped to it. Without it statistics fall back to the
   * user's first admin membership.
   */
  useActiveDistrictId?: () => string | null
}

export function buildStatisticsRoutes<TParent extends AnyRoute>(
  parent: TParent,
  options: StatisticsRoutesOptions = {},
) {
  registerStatisticsI18n()
  const { useActiveDistrictId } = options
  // A pathless layout around every statistics page, so all of them read the same area.
  const area = createRoute({
    getParentRoute: () => parent,
    id: 'statistics-area',
    component: () =>
      useActiveDistrictId ? (
        <ActiveDistrictProvider useActiveDistrictId={useActiveDistrictId}>
          <Outlet />
        </ActiveDistrictProvider>
      ) : (
        <Outlet />
      ),
  })
  return [area.addChildren(statisticsPages(area))]
}

function statisticsPages<TParent extends AnyRoute>(parent: TParent) {
  return [
    // /statistics → /statistics/reports
    createRoute({
      getParentRoute: () => parent,
      path: '/statistics',
      beforeLoad: () => {
        throw redirect({ to: '/statistics/reports' as never })
      },
      component: () => null,
    }),
    // /statistics/reports
    createRoute({
      getParentRoute: () => parent,
      path: '/statistics/reports',
      component: lazyRouteComponent(
        () => import('./features/reports/StatisticsPage'),
        'StatisticsPage',
      ),
    }),
    // /statistics/table
    createRoute({
      getParentRoute: () => parent,
      path: '/statistics/table',
      component: lazyRouteComponent(
        () => import('./features/table/StatisticsTablePage'),
        'StatisticsTablePage',
      ),
    }),
    // /statistics/observation-reports
    createRoute({
      getParentRoute: () => parent,
      path: '/statistics/observation-reports',
      // Normalises `?species=` at the route boundary, so a stale or
      // hand-edited value resolves to the default tab before the page renders.
      validateSearch: (s: Record<string, unknown>) => ({
        species: parseSpeciesSlug(s.species),
      }),
      component: lazyRouteComponent(
        () => import('./features/observation-reports/ObservationReportsPage'),
        'ObservationReportsPage',
      ),
    }),
    // /statistics/other-reports
    createRoute({
      getParentRoute: () => parent,
      path: '/statistics/other-reports',
      component: lazyRouteComponent(
        () => import('./features/other-reports/OtherReportsPage'),
        'OtherReportsPage',
      ),
    }),
    // /statistics/advanced
    createRoute({
      getParentRoute: () => parent,
      path: '/statistics/advanced',
      component: lazyRouteComponent(
        () => import('./features/advanced/AdvancedStatisticsPage'),
        'AdvancedStatisticsPage',
      ),
    }),
  ]
}
